import type { ServerFetchMapping } from "./types";

/**
 * Common args for the global get_item_details function.
 * Each DocType's buildArgs spreads this and adds DocType-specific fields.
 */
function commonArgs(
  triggerValue: string,
  row: Record<string, any>,
  parentDoc: Record<string, any>,
  parentDoctype: string
): Record<string, any> {
  return {
    item_code: triggerValue,
    doctype: parentDoctype,
    name: parentDoc.name || "",
    company: parentDoc.company || "",
    qty: row.qty || 1,
    uom: row.uom || "",
    conversion_factor: row.conversion_factor || "",
    warehouse:
      row.warehouse ||
      row.s_warehouse ||
      row.t_warehouse ||
      parentDoc.set_warehouse ||
      "",
    currency: parentDoc.currency || "",
    conversion_rate: parentDoc.conversion_rate || 1,
    plc_conversion_rate: parentDoc.plc_conversion_rate || 1,
    posting_date: parentDoc.posting_date || parentDoc.transaction_date || "",
    transaction_date:
      parentDoc.transaction_date || parentDoc.posting_date || "",
    tax_category: parentDoc.tax_category || "",
  };
}

/**
 * Static configuration map: parent DocType → server fetch mappings.
 *
 * Each entry defines which child table field triggers a server call,
 * which method to call, and how to construct the server method args.
 *
 * The buildArgs callback constructs the full args dict sent to the server.
 * parentContextFields is kept for documentation of which parent fields matter.
 */
export const SERVER_FETCH_CONFIG: Record<string, ServerFetchMapping[]> = {
  "Stock Entry": [
    {
      triggerField: "item_code",
      childDoctype: "Stock Entry Detail",
      // Use a thin wrapper that calls StockEntry.get_item_details() directly.
      // The global get_item_details hardcodes child_doctype = doctype + " Item",
      // but Stock Entry's child is "Stock Entry Detail" (causes 417 error).
      method: "zaviago_backend.api.stock_entry.get_stock_entry_item_details",
      parentContextFields: ["company", "stock_entry_type", "posting_date"],
      buildArgs: (triggerValue, row, parentDoc) => ({
        args: JSON.stringify({
          item_code: triggerValue,
          warehouse: row.s_warehouse || row.t_warehouse || "",
          transfer_qty: row.transfer_qty || 0,
          serial_no: row.serial_no || "",
          batch_no: row.batch_no || "",
          bom_no: row.bom_no || "",
          expense_account: row.expense_account || "",
          cost_center: row.cost_center || "",
          company: parentDoc.company || "",
          qty: row.qty || 1,
          voucher_type: "Stock Entry",
          allow_zero_valuation: 1,
        }),
        doc_json: JSON.stringify({
          company: parentDoc.company || "",
          stock_entry_type: parentDoc.stock_entry_type || "",
          purpose: parentDoc.purpose || "",
          posting_date: parentDoc.posting_date || "",
          posting_time: parentDoc.posting_time || "",
          set_warehouse: parentDoc.set_warehouse || "",
          from_warehouse: parentDoc.from_warehouse || "",
          to_warehouse: parentDoc.to_warehouse || "",
        }),
      }),
    },
  ],

  "Sales Invoice": [
    {
      triggerField: "item_code",
      childDoctype: "Sales Invoice Item",
      method: "erpnext.stock.get_item_details.get_item_details",
      parentContextFields: [
        "company",
        "customer",
        "posting_date",
        "currency",
        "selling_price_list",
      ],
      buildArgs: (triggerValue, row, parentDoc, parentDoctype) => ({
        args: {
          ...commonArgs(triggerValue, row, parentDoc, parentDoctype),
          customer: parentDoc.customer || "",
          selling_price_list: parentDoc.selling_price_list || "",
          price_list_currency: parentDoc.price_list_currency || "",
          is_return: parentDoc.is_return || 0,
          update_stock: parentDoc.update_stock || 0,
          is_pos: parentDoc.is_pos || 0,
        },
      }),
    },
  ],

  "Purchase Order": [
    {
      triggerField: "item_code",
      childDoctype: "Purchase Order Item",
      method: "erpnext.stock.get_item_details.get_item_details",
      parentContextFields: [
        "company",
        "supplier",
        "transaction_date",
        "currency",
        "buying_price_list",
      ],
      buildArgs: (triggerValue, row, parentDoc, parentDoctype) => ({
        args: {
          ...commonArgs(triggerValue, row, parentDoc, parentDoctype),
          supplier: parentDoc.supplier || "",
          buying_price_list: parentDoc.buying_price_list || "",
          price_list_currency: parentDoc.price_list_currency || "",
          is_subcontracted: parentDoc.is_subcontracted || 0,
          ignore_pricing_rule: parentDoc.ignore_pricing_rule || 0,
        },
      }),
    },
  ],

  "Delivery Note": [
    {
      triggerField: "item_code",
      childDoctype: "Delivery Note Item",
      method: "erpnext.stock.get_item_details.get_item_details",
      parentContextFields: [
        "company",
        "customer",
        "posting_date",
        "currency",
        "selling_price_list",
      ],
      buildArgs: (triggerValue, row, parentDoc, parentDoctype) => ({
        args: {
          ...commonArgs(triggerValue, row, parentDoc, parentDoctype),
          customer: parentDoc.customer || "",
          selling_price_list: parentDoc.selling_price_list || "",
          price_list_currency: parentDoc.price_list_currency || "",
          is_return: parentDoc.is_return || 0,
        },
      }),
    },
  ],

  "Purchase Receipt": [
    {
      triggerField: "item_code",
      childDoctype: "Purchase Receipt Item",
      method: "erpnext.stock.get_item_details.get_item_details",
      parentContextFields: [
        "company",
        "supplier",
        "posting_date",
        "currency",
        "buying_price_list",
      ],
      buildArgs: (triggerValue, row, parentDoc, parentDoctype) => ({
        args: {
          ...commonArgs(triggerValue, row, parentDoc, parentDoctype),
          supplier: parentDoc.supplier || "",
          buying_price_list: parentDoc.buying_price_list || "",
          price_list_currency: parentDoc.price_list_currency || "",
          is_subcontracted: parentDoc.is_subcontracted || 0,
        },
      }),
    },
  ],
};

/**
 * Fields from server response that must never overwrite row data.
 * These are Frappe system fields or row identity fields.
 */
export const PROTECTED_FIELDS = new Set([
  "idx",
  "name",
  "doctype",
  "parent",
  "parenttype",
  "parentfield",
  "owner",
  "creation",
  "modified",
  "modified_by",
]);
